import React from 'react';
import _ from 'lodash';
import log from '@wix/wix-log';
import type { NextPage } from 'next';
import Head from 'next/head';
import {
  HydrationBoundary,
  QueryClient,
  dehydrate,
} from '@tanstack/react-query';
import { web } from '@wix/wix-common-aspects';
import { WixDesignSystemProvider } from '@wix/design-system';
import { Service } from '@wix/ambassador-category-services/types';
import {
  WixNextApiRequest,
  WixNextApiResponse,
  IParams,
  Filters,
  SITE_TYPES,
  INDUSTRIES,
  FEATURES,
  STYLES,
  PRICES,
  SortingMethod,
  SEOProps,
} from '@common/types';
import { getCleanUrl } from '@utils/urlUtils';
import { isValidSortingMethod } from '@utils/typeUtils';
import { TemplatesContextProvider } from '@components/Contexts/TemplatesContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import Templates from '@components/Templates';
import { queryKeyFrom } from '@utils/queryUtils';
import createTemplatesSeoAPI from '@services/TemplatesSeoAPI';
import { getWixContext } from '@utils/async-store';
import {
  ENABLE_MARKETPLACE_TEMPLATES_SPEC,
  ENABLE_TEMPLATES_CURRENCY_CONVERSION_SPEC,
  TAX_INCLUSIVE_COUNTRIES,
} from '@utils/constants';
import { getPageLanguage } from '@app/utils';

const TemplatesPage: NextPage<{
  services: Service[];
  categoriesForHeader: Array<Service> | undefined;
  dehydratedState: any;
  numberOfResults: number;
  numberOfPages: number;
  isMobileUserAgent: boolean;
  origin: string;
  entry: any;
  host: string;
  seo: SEOProps;
  filters: Filters;
  currentPage: number;
  sort: SortingMethod;
  userCurrency: string;
  currencyConversionRate: number;
  taxRate: number;
  error?: any;
}> = ({
  isMobileUserAgent,
  dehydratedState,
  numberOfResults,
  numberOfPages,
  origin,
  entry,
  host,
  services,
  categoriesForHeader,
  seo: { title, metaTags, links, gtm },
  filters,
  currentPage,
  sort,
  userCurrency,
  currencyConversionRate,
  taxRate,
  error,
}) => {
  const isProd = process.env.NODE_ENV === 'production';

  const TemplatesHeader = () => (
    <Head>
      <title>{title}</title>
      {_.map(links, (attr, index) =>
        attr.rel === 'canonical' ? null : (
          <link
            hrefLang={attr.hrefLang ? attr.hrefLang : 'x-default'}
            rel={attr.rel}
            href={attr.href}
            key={`lang-${index}`}
          />
        ),
      )}
      {_.map(metaTags, (attr, index) => (
        <meta {...attr} key={`meta-${index}`} />
      ))}
      <link
        rel="canonical"
        href={_.get(_.find(links, { rel: 'canonical' }), 'href')}
      ></link>
      {isProd && <script dangerouslySetInnerHTML={{ __html: gtm }}></script>}
    </Head>
  );

  return (
    <>
      <TemplatesHeader />
      <BiSessionContextProvider>
        <CurrencyContextProvider
          currencyData={{
            currencyCode: userCurrency,
            conversionRate: currencyConversionRate,
          }}
          taxRate={taxRate}
        >
          <HeaderContextProvider
            services={categoriesForHeader || services}
            host={host}
          >
            <HydrationBoundary state={dehydratedState}>
              <TemplatesContextProvider
                numberOfResults={numberOfResults}
                numberOfPages={numberOfPages}
                isMobileUserAgent={isMobileUserAgent}
                origin={origin}
                entry={entry}
                host={host}
                filters={filters}
                currentPage={currentPage}
                sort={sort}
                error={error}
              >
                <WixDesignSystemProvider features={{ newColorsBranding: true }}>
                  <Templates />
                </WixDesignSystemProvider>
              </TemplatesContextProvider>
            </HydrationBoundary>
          </HeaderContextProvider>
        </CurrencyContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

function getSiteTypeFilterFromUrl(query: IParams) {
  const siteTypeFromUrl = query.siteType as string;
  const siteType =
    SITE_TYPES[_.toUpper(siteTypeFromUrl) as keyof typeof SITE_TYPES] ??
    undefined;
  return siteType === 'ALL' ? undefined : siteType;
}

function getSearchFilterFromUrl(query: IParams) {
  const searchFromUrl = query.search;
  if (typeof searchFromUrl !== 'string') {
    return null;
  }
  const searchTerm = searchFromUrl.trim();
  return searchTerm;
}

function getLanguageFilterFromUrl(query: IParams, languages: any) {
  const languageFromUrl = query.language as string;
  const language = _.get(_.find(languages, { id: languageFromUrl }), 'id');
  return language === 'ALL' ? null : language;
}

function getIndustryFilterFromUrl(query: IParams) {
  const industriesFromUrl = query.industries as string;
  const industries = _.isUndefined(industriesFromUrl)
    ? null
    : industriesFromUrl.split(',');

  return _.reduce(
    industries,
    (acc: string[], industry) => {
      if (INDUSTRIES[_.toUpper(industry) as keyof typeof INDUSTRIES]) {
        acc.push(industry);
      }
      return acc;
    },
    [],
  );
}

function getFeaturesFilterFromUrl(query: IParams) {
  const featuresFromUrl = query.features as string;
  const features = _.isUndefined(featuresFromUrl)
    ? null
    : featuresFromUrl.split(',');

  return _.reduce(
    features,
    (acc: string[], feature) => {
      if (FEATURES[_.toUpper(feature) as keyof typeof FEATURES]) {
        acc.push(feature);
      }
      return acc;
    },
    [],
  );
}

function getMultiLanguagesFilterFromUrl(
  query: IParams,
  supportedLanguages: any,
) {
  const langsFromUrl = query.languages as string;
  const langs = _.isUndefined(langsFromUrl) ? null : langsFromUrl.split(',');

  return _.reduce(
    langs,
    (acc: string[], lang) => {
      const languageObj = _.get(_.find(supportedLanguages, { id: lang }), 'id');
      acc.push(languageObj);
      return acc;
    },
    [],
  );
}

function getPriceParamsFilterFromUrl(query: IParams): any {
  const priceFromUrl = query.price as string;
  const price =
    PRICES[_.toUpper(priceFromUrl) as keyof typeof PRICES] ?? undefined;
  return price === 'ALL' ? undefined : price;
}

function getStyleFilterFromUrl(query: IParams) {
  const stylesFromUrl = query.styles as string;
  const styles = _.isUndefined(stylesFromUrl) ? null : stylesFromUrl.split(',');

  return _.reduce(
    styles,
    (acc: string[], style) => {
      if (STYLES[_.toUpper(style) as keyof typeof STYLES]) {
        acc.push(style);
      }
      return acc;
    },
    [],
  );
}

function getSortFromUrl(query: IParams): any {
  const sortFromUrl = query.sort as string;
  return isValidSortingMethod(sortFromUrl) ? sortFromUrl : 'relevance';
}

export async function getServerSideProps({
  req,
  res,
  query,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  query: IParams;
}) {
  const wixContext = getWixContext();
  const petriServiceAPI = wixContext.serviceFactory.petriServiceAPI();
  const isTemplatesMarketplaceExperimentEnabled =
    await petriServiceAPI.isExperimentEnabled(
      ENABLE_MARKETPLACE_TEMPLATES_SPEC,
    );

  if (!isTemplatesMarketplaceExperimentEnabled) {
    return {
      redirect: {
        permanent: false,
        destination: 'https://www.wix.com/marketplace',
      },
    };
  }

  const language = getPageLanguage({
    aspects: req.aspects,
    pageName: 'templates',
  });
  const url = req.aspects?.get(web.Url);
  const geo = req.aspects?.get(web.Geo);
  const alpha2CountryCode = geo?.['2lettersCountryCode'];
  const servicesAPI = wixContext.serviceFactory.servicesAPI();
  const languagesAPI = wixContext.serviceFactory.languagesAPI();
  const templateListingAPI = wixContext.serviceFactory.templateListingAPI();
  const currencyAPI = wixContext.serviceFactory.currencyAPI();
  const taxCalculationAPI = wixContext.serviceFactory.taxCalculationAPI();

  const isCurrencyConversionExperimentEnabled =
    await petriServiceAPI.isExperimentEnabled(
      ENABLE_TEMPLATES_CURRENCY_CONVERSION_SPEC,
    );

  const { entry, origin } = query;
  const page = query.page as string;
  const serviceIds = query.serviceIds as string;
  try {
    const [allServicesWithHierarchy, categoriesForHeader, languages] =
      await Promise.all([
        servicesAPI.getAllServicesWithHierarchy({
          withPathName: true,
          withTranslatedKey: true,
          language,
        }),
        servicesAPI.getCategoriesForHeader({
          withPathName: true,
          withTranslatedKey: true,
          language,
        }),
        languagesAPI.getLanguages(),
      ]);

    const selectedSiteType = getSiteTypeFilterFromUrl(query);
    const industries = getIndustryFilterFromUrl(query);
    const features = getFeaturesFilterFromUrl(query);
    const selectedLanguages = getMultiLanguagesFilterFromUrl(query, languages);
    const selectedLanguage = getLanguageFilterFromUrl(query, languages);
    const styles = getStyleFilterFromUrl(query);
    const selectedPrice = getPriceParamsFilterFromUrl(query);
    const currentPage = _.toNumber(page) || 1;
    const sort = getSortFromUrl(query);
    const search = getSearchFilterFromUrl(query);

    const _language = selectedLanguage || 'ALL';
    const siteType = selectedSiteType || 'ALL';
    const price = selectedPrice || 'ALL';

    const filters = {
      languages: selectedLanguages,
      language: _language,
      features,
      industries,
      siteType,
      styles,
      price,
      search,
    } as any;

    const queryClient = new QueryClient();
    const createQueryKey = ({ isInfinite = false }) =>
      queryKeyFrom(filters, isInfinite ? 1 : currentPage, sort, isInfinite);

    const createQueryFn =
      ({ isInfinite = false }) =>
      async () => {
        const { templates, numberOfPages, count, hasNextPage } =
          await templateListingAPI.queryTemplates({
            ...filters,
            page: isInfinite ? 1 : currentPage,
            sort,
          });
        return {
          templates,
          numberOfPages,
          count,
          hasNextPage,
        };
      };

    const templatesSeoAPI = createTemplatesSeoAPI(
      res.locals.appContext,
      req.aspects,
    );

    await Promise.all([
      queryClient.prefetchInfiniteQuery({
        queryKey: createQueryKey({ isInfinite: true }),
        queryFn: createQueryFn({ isInfinite: true }),
        initialPageParam: 1,
      }),
      queryClient.prefetchQuery({
        queryKey: createQueryKey({ isInfinite: false }),
        queryFn: createQueryFn({ isInfinite: false }),
      }),
    ]);

    const { userCurrency, currencyConversionRate } =
      isCurrencyConversionExperimentEnabled
        ? await currencyAPI.getCurrencyAndConversionRateSupportedByEcom({
            alpha2CountryCode,
          })
        : currencyAPI.getDefaultCurrencyAndConversionRate();
    // returns taxRate = 0 for non included countries
    let countryTaxRate = 0;
    const countryCode = geo?.['2lettersCountryCode'] || '';
    try {
      if (
        TAX_INCLUSIVE_COUNTRIES.includes(countryCode) &&
        isCurrencyConversionExperimentEnabled
      ) {
        const { taxRate } = await taxCalculationAPI.getTaxRate(
          countryCode,
          userCurrency,
        );
        countryTaxRate = taxRate ?? 0;
      }
    } catch (error: any) {
      log('templates').withAspects(req.aspects).info('getCountryTaxRate', {
        message: error.message,
      });
    }

    const cleanUrl = getCleanUrl(allServicesWithHierarchy, url, serviceIds);
    const [title, metaTags, links, gtm] = await Promise.all([
      templatesSeoAPI.getPageTitle(language),
      templatesSeoAPI.getPageMetaTags(cleanUrl, language),
      templatesSeoAPI.getPageLinkTags(cleanUrl),
      templatesSeoAPI.getGTM(),
    ]);

    const props = {
      services: allServicesWithHierarchy,
      categoriesForHeader,
      host: `https://${req.get('host')}`,
      seo: {
        title,
        metaTags,
        links,
        gtm,
      },
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      ...(origin && { origin }),
      ...(entry && { entry }),
      ...(filters && { filters }),
      currentPage,
      ...(sort && { sort }),
      userCurrency,
      currencyConversionRate,
      taxRate: countryTaxRate,
    };
    return {
      props,
    };
  } catch (e: any) {
    const { message, stack } = e;
    log('templates')
      .withAspects(req.aspects)
      .error(`error w/ templates`, { message, stack });
    return {
      props: {
        error: message,
        seo: {
          title: `Wix Templates Marketplace`,
        },
      },
    };
  }
}

export default TemplatesPage;
